import React from "react";
import { Column } from "../components/Layout";
import { Signature } from "../components/Signature";
import { FadeIn } from "../components/FadeIn";
import { Seo } from "../components/Seo";
import { OutlineLink } from "../components/OutlineLink";

const PHONE = "+27 79 281 8435";
const PHONE_HREF = "https://wa.me/27792818435";

const FinalTribute = () => {
  return (
    <div data-testid="final-tribute-page">
      <Seo
        title="The Final Tribute · Funeral Tributes & Obituaries · The Raven's Quill"
        description="A funeral tribute written in seventy-two hours: the eulogy prepared for public delivery, and print-ready obituary copy for the funeral booklet, made with anthropological care."
        path="/final-tribute"
      />

      {/* PAGE HEADER */}
      <section className="pt-20 pb-16 md:pt-28 md:pb-20">
        <Column>
          <Signature label="The Final Tribute" testid="final-tribute-signature" />
          <FadeIn>
            <h1 className="text-[32px] md:text-[38px] italic leading-[1.25] text-[#1C1A17]" data-testid="final-tribute-headline">
              The Final Tribute
            </h1>
          </FadeIn>
          <div className="mt-10 space-y-7 text-[17px] md:text-[18px] leading-[1.8] text-[#1C1A17]">
            <FadeIn as="p">
              There comes a moment when a life can no longer be studied over months or
              years, but still asks to be understood faithfully.
            </FadeIn>
            <FadeIn as="p">
              The Final Tribute is The Raven&rsquo;s Quill&rsquo;s second practice of
              bespoke legacy preservation. It is written for the narrow window between
              death and farewell, when a family is called upon to give public voice to a
              life that can no longer speak for itself.
            </FadeIn>
          </div>
        </Column>
      </section>

      {/* SPECIMEN, IN THE FUNERAL PROGRAMME */}
      <section className="pb-16 md:pb-20" data-testid="tribute-specimen-section">
        <Column>
          <FadeIn>
            <div className="border border-[#7A2E2E] p-2 sm:p-3 bg-[#F4F1EA]">
              <img
                src="https://static.prod-images.emergentagent.com/jobs/52749592-c014-402b-8984-74e3e9024749/images/ebfac24c35fd7343740451936ca3a0840e303a4ca240f63d6263ece771bd8ced.jpeg"
                alt="A Final Tribute leaflet, printed on antique paper, resting as a loose insert on an open white funeral programme on a chair at the service"
                className="w-full h-auto"
                loading="lazy"
                data-testid="tribute-specimen-image"
              />
            </div>
          </FadeIn>
          <FadeIn>
            <p
              className="mt-5 text-center text-[11px] uppercase tracking-[0.32em] text-[#7A2E2E]"
              data-testid="tribute-specimen-caption"
            >
              How a Final Tribute sits in the family&#8217;s funeral programme
            </p>
          </FadeIn>
        </Column>
      </section>

      {/* PULL-LINE */}
      <section className="pb-4">
        <Column>
          <FadeIn>
            <hr className="border-0" style={{ width: "72px", height: "0.75px", backgroundColor: "#7A2E2E" }} />
            <p className="mt-8 text-[19px] md:text-[21px] italic leading-[1.6] text-[#1C1A17]" data-testid="final-tribute-pullline">
              This is not speechwriting.
            </p>
          </FadeIn>
        </Column>
      </section>

      <section className="pb-16 md:pb-20">
        <Column>
          <div className="space-y-7 text-[17px] md:text-[18px] leading-[1.8] text-[#1C1A17]">
            <FadeIn as="p">
              It is not the assembly of anecdotes into a moving address, nor the
              production of a funeral programme. It is a rapid anthropological inquiry
              undertaken within the time that ritual allows. Over the course of
              seventy-two hours, we enter the world the deceased leaves behind: listening
              to those who knew them, reading the photographs they kept, attending to the
              objects, relationships, language and memories that made a particular life
              unmistakably their own.
            </FadeIn>
            <FadeIn as="p">Only then do we write.</FadeIn>
            <FadeIn as="p">
              The result is a tribute that seeks not merely to recount what happened, but
              to answer a quieter question: who was this person, and what did the world
              become because they lived?
            </FadeIn>
          </div>
        </Column>
      </section>

      {/* THE BOOKLET AND THE JOURNEY */}
      <section className="pb-16 md:pb-20">
        <Column>
          <Signature label="The Booklet and the Journey" testid="booklet-signature" />
          <div className="space-y-7 text-[17px] md:text-[18px] leading-[1.8] text-[#1C1A17]">
            <FadeIn as="p">
              In many South African traditions, the funeral booklet is not simply
              retained as a record. It accompanies the deceased as part of the final rite
              of passage, before passing out of the hands of the living. The words
              therefore carry a double responsibility. They must help a family recognise
              the life they have loved, while also being worthy of the journey they
              symbolically accompany.
            </FadeIn>
            <FadeIn as="p">
              That responsibility shapes every commission. The task is not to embellish a
              life, nor to smooth away its complexity. It is to understand carefully, to
              write truthfully, and to exercise restraint where restraint honours both
              the deceased and those who remain.
            </FadeIn>
          </div>
        </Column>
      </section>

      {/* HOW THE WORK IS MADE */}
      <section className="pb-16 md:pb-20">
        <Column>
          <Signature label="The Field" testid="the-field-signature" />
          <FadeIn as="p" className="text-[17px] md:text-[18px] leading-[1.8] text-[#1C1A17]">
            Every Final Tribute begins with conversation. Family members, friends,
            photographs, letters, voice notes, messages, and small remembered details
            become the field from which the work is written. Within the constraints of
            time, we undertake the same discipline that guides The Life Study: listening
            before interpreting, understanding before writing.
          </FadeIn>
        </Column>
      </section>

      {/* WHAT IS DELIVERED */}
      <section className="pb-16 md:pb-20">
        <Column>
          <Signature label="What Is Delivered" testid="tribute-delivered-signature" />
          <FadeIn as="p" className="text-[17px] md:text-[18px] leading-[1.8] text-[#1C1A17]">
            The completed work is delivered within seventy-two hours: a professionally
            written tribute suitable for public delivery, together with print-ready copy
            for inclusion in the funeral booklet or memorial programme.
          </FadeIn>
          <FadeIn className="mt-10">
            <OutlineLink to="/from-the-booklet" testid="tribute-sample-cta">
              Read a Final Tribute Sample
            </OutlineLink>
          </FadeIn>
        </Column>
      </section>


      {/* THE TWO PRACTICES */}
      <section className="pb-16 md:pb-20">
        <Column>
          <FadeIn as="p" className="text-[17px] md:text-[18px] leading-[1.8] text-[#1C1A17]">
            The Final Tribute exists alongside The Life Study as the second expression of
            The Raven&rsquo;s Quill. The Life Study asks how a life should be understood
            across generations. The Final Tribute asks how a life should be entrusted at
            the moment of farewell. Both are acts of bespoke legacy preservation, and
            both are written with the same conviction: a life of consequence deserves to
            be understood with care.
          </FadeIn>
        </Column>
      </section>

      {/* TO BEGIN */}
      <section id="to-begin" className="pb-24 md:pb-32">
        <Column>
          <Signature label="To Begin" testid="to-begin-signature" />
          <FadeIn as="p" className="text-[17px] md:text-[18px] leading-[1.8] text-[#1C1A17]">
            A Final Tribute begins with a telephone call or WhatsApp message.
          </FadeIn>
          <FadeIn>
            <a
              href={PHONE_HREF}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="final-tribute-phone"
              className="mt-8 inline-block text-[26px] md:text-[30px] italic text-[#1C1A17] hover:text-[#7A2E2E] transition-colors"
            >
              {PHONE}
            </a>
          </FadeIn>
          <FadeIn as="p" className="mt-8 text-[17px] md:text-[18px] leading-[1.8] text-[#1C1A17]">
            The fee is fixed, and given plainly in that first conversation. Discretion is
            assumed from the first word.
          </FadeIn>
        </Column>
      </section>
    </div>
  );
};

export default FinalTribute;
